import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Theme store — Zustand s localStorage persist.
 *
 * Tři režimy:
 *   - 'light' / 'dark' — explicit volba uživatele
 *   - 'system'         — sleduje `prefers-color-scheme` media query
 *
 * `applyTheme(resolved)` přepne `<html class="dark">` resp. odebere class.
 * Voláno při hydraci store, při změně přes setTheme, a při změně OS media
 * query (pokud režim = 'system').
 */
export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = "axigon.theme";

function getPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === "system") return getPrefersDark() ? "dark" : "light";
  return theme;
}

export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.dataset.theme = resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => {
        set({ theme });
        applyTheme(resolveTheme(theme));
      },
    }),
    {
      name: STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(resolveTheme(state.theme));
      },
    },
  ),
);

/**
 * Initialize theme on app boot. Listens to prefers-color-scheme changes
 * (only effective when current mode = 'system').
 */
export function initTheme(): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => undefined;
  }
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    const { theme } = useThemeStore.getState();
    if (theme === "system") applyTheme(resolveTheme("system"));
  };
  mql.addEventListener("change", handler);
  applyTheme(resolveTheme(useThemeStore.getState().theme));
  return () => mql.removeEventListener("change", handler);
}
